import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useMemo, useState } from 'react';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import MedicalTimelineCard from '../../molecules/MedicalTimelineCard';
import componentConstants from '../../../core-utils/messages/componentConstants';

export interface RiskPatientDetailsCardProps {
  icon: React.ReactNode;
  diagnosis: string;
  provider: string;
  claimId: string;
  amountPaid: string | number;
  claimType: string;
  medication: string;
  avgRatevsCMS: string;
  totalRate: string;
}

export interface RiskPatientDataDetails {
  date: string;
  memberID?: string;
  riskPatientCardDetails: RiskPatientDetailsCardProps[];
}
export interface MedicalHistoryDetailsProps {
  riskPatientDetails: RiskPatientDataDetails[];
  initialVisibleCount?: number;
  loadMoreStep?: number;
  isBenchmarkPage?: boolean;
}

const MedicalHistoryDetails: React.FC<MedicalHistoryDetailsProps> = ({
  riskPatientDetails,
  initialVisibleCount = 10,
  loadMoreStep = 10,
  isBenchmarkPage = false,
}) => {
  const useStyles = makeStyles()({
    root: {
      display: 'flex',
      flexDirection: 'column',
    },
    horizontalLine: {
      borderTop: '1px dashed' + patientAccessTheme.palette.grey[300],
      width: '966px',
    },
    mainBody: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '123px',
      marginBottom: '20px',
    },
    text: {
      textDecoration: 'dashed',
      textDecorationLine: 'underline',
      textDecorationThickness: '0.5px',
      textUnderlineOffset: '5px',
    },
  });

  const { classes } = useStyles();
  const [visibleCount, setVisibleCount] = useState<number>(initialVisibleCount);

  const visibleData = useMemo(() => riskPatientDetails.slice(0, visibleCount), [
    riskPatientDetails,
    visibleCount,
  ]);
  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + loadMoreStep);
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column' }}>
      <Box data-Testid="MedicalHistoryDetails">
        {visibleData.map((item, index: number) => {
          return (
            <Grid container className={classes.root} key={index}>
              <Grid>
                <Typography
                  fontSize="12px"
                  lineHeight="24px"
                  fontType="semiBold"
                  fontFamily="Inter"
                  textColor={patientAccessTheme.palette.text.mediumemphasis}
                  margin="0px 0px 8px 2px"
                >
                  {item.date}
                </Typography>
              </Grid>
              <Grid className={classes.horizontalLine} />
              <Grid className={classes.mainBody}>
                {item.riskPatientCardDetails?.map((patientDetails, index) => {
                  return (
                    <Grid key={index}>
                      <Grid
                        style={{
                          width: '1px',
                          height: index === 0 ? '30px' : '36px',
                          marginLeft: '41.5px',
                          background: patientAccessTheme.palette.grey[300],
                        }}
                      />
                      <MedicalTimelineCard
                        icon={patientDetails?.icon}
                        diagnosis={patientDetails?.diagnosis}
                        provider={patientDetails?.provider}
                        claimId={patientDetails?.claimId}
                        totalAmountPaid={patientDetails?.amountPaid as string}
                        memberID={item.memberID!}
                        claimType={patientDetails?.claimType}
                        medication={patientDetails?.medication}
                        avgRatevsCMS={patientDetails?.avgRatevsCMS}
                        totalRate={patientDetails?.totalRate}
                        isBenchmarkPage={isBenchmarkPage}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          );
        })}
      </Box>
      {visibleCount < riskPatientDetails.length && (
        <Box
          style={{
            display: 'flex',
            marginBottom: '20px',
            justifyContent: 'center',
            paddingRight: '275px',
          }}
        >
          <Typography
            fontSize="14px"
            lineHeight="28px"
            fontType="medium"
            textColor={patientAccessTheme.palette.primary[500]}
            className={classes.text}
            cursor="pointer"
            onClick={handleLoadMore}
          >
            {componentConstants.MOLECULES.MEDICAL_HISTORY_TIMELINE.LOAD_MORE}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MedicalHistoryDetails;
